import { useCallback, useEffect, useRef, useState } from 'react';

export const useInfiniteScroll = (loadMore, options = {}) => {
  const {
    hasMore = true,
    threshold = 100,
    rootMargin = '100px',
    isReverse = false,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false); // Use ref for immediate checks (avoids stale closure)
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  const handleIntersection = useCallback(async (entries) => {
    const [entry] = entries;

    // Use ref for immediate check to prevent multiple rapid calls (stale closure fix)
    if (entry.isIntersecting && hasMore && !isLoadingRef.current) {
      isLoadingRef.current = true;
      setIsLoading(true);

      try {
        if (loadMore) {
          await loadMore();
        }
      } catch (error) {
        console.error('Infinite scroll load failed:', error);
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [hasMore, loadMore]); // Removed isLoading from deps since we use ref now

  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(handleIntersection, {
      root: null, // viewport
      rootMargin,
      threshold: 0.1,
    });

    observer.observe(sentinelRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, rootMargin]);

  // Update observer when hasMore changes
  useEffect(() => {
    if (observerRef.current && !hasMore) {
      observerRef.current.disconnect();
    }
  }, [hasMore]);

  return {
    sentinelRef,
    isLoading,
  };
};
